import { useState, useMemo } from 'react';
import { Check, X, RotateCcw, Truck, Package, ShoppingCart } from 'lucide-react';

/* ─── Types ─── */
export interface PurchaseEvent {
  status: string;
  previous_status: string | null;
  metadata: {
    asin?: string;
    unit_cost?: number;
    quantity?: number;
    tracking_number?: string;
    carrier?: string;
    delivery_date?: string;
  };
  created_at: string;
}

export interface PurchaseTrackerProps {
  events: PurchaseEvent[];
  currentStatus: string;
  compact?: boolean;
  trackingNumber?: string;
  carrier?: string;
  unitCost?: number;
  deliveryDate?: string;
}

/* ─── Step definitions ─── */
interface Step {
  key: string;
  label: string;
  statuses: string[];
  icon: typeof ShoppingCart;
}

const STEPS: Step[] = [
  { key: 'ordered',   label: 'Comprado',  statuses: ['ordered', 'confirmed', 'needs_review', 'pending_approval', 'safeguard_hold'], icon: ShoppingCart },
  { key: 'shipped',   label: 'Enviado',   statuses: ['shipped'],                              icon: Package },
  { key: 'in_transit', label: 'En Camino', statuses: ['in_transit', 'out_for_delivery'],       icon: Truck },
  { key: 'delivered', label: 'Entregado', statuses: ['delivered', 'delivered_ml'],             icon: Check },
];

const RETURNED_STEP: Step = {
  key: 'returned', label: 'Devuelto', statuses: ['returned'], icon: RotateCcw,
};

const CANCELLED_STATUSES = ['cancelled', 'error', 'failed'];

type StepState = 'completed' | 'active' | 'pending' | 'error' | 'returned';

/* ─── Helpers ─── */
function getStepIndex(status: string): number {
  for (let i = 0; i < STEPS.length; i++) {
    if (STEPS[i].statuses.includes(status)) return i;
  }
  return -1;
}

function fmtShortDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  } catch {
    return '';
  }
}

function fmtFullDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleString('es-ES', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '';
  }
}

/* ─── Core logic ─── */
function computeSteps(events: PurchaseEvent[], currentStatus: string) {
  const isCancelled = CANCELLED_STATUSES.includes(currentStatus);
  const isReturned = currentStatus === 'returned';
  const currentIdx = getStepIndex(currentStatus);

  // Build event map: which steps have events
  const eventByStep = new Map<string, PurchaseEvent>();
  for (const ev of events) {
    for (const step of STEPS) {
      if (step.statuses.includes(ev.status) && !eventByStep.has(step.key)) {
        eventByStep.set(step.key, ev);
      }
    }
    if (ev.status === 'returned') {
      eventByStep.set('returned', ev);
    }
  }

  // Determine active index from events or fallback to currentStatus
  let activeIdx = currentIdx;
  if (events.length > 0) {
    // Find the highest step that has an event
    for (let i = STEPS.length - 1; i >= 0; i--) {
      if (eventByStep.has(STEPS[i].key)) {
        activeIdx = i;
        break;
      }
    }
  }

  // Build steps with states
  type ComputedStep = Step & { state: StepState; event: PurchaseEvent | null };
  const steps: ComputedStep[] = STEPS.map((step, i) => {
    let state: StepState = 'pending';

    if (isCancelled) {
      if (i < activeIdx) state = 'completed';
      else if (i === activeIdx) state = 'error';
      // rest stay pending
    } else if (i < activeIdx) {
      state = 'completed';
    } else if (i === activeIdx) {
      // If we're at the last meaningful step AND currentStatus matches, it's completed
      if (step.statuses.includes(currentStatus) && (currentStatus === 'delivered' || currentStatus === 'delivered_ml')) {
        state = 'completed';
      } else {
        state = 'active';
      }
    }

    return {
      ...step,
      state,
      event: eventByStep.get(step.key) || null,
    };
  });

  // Add returned step if applicable
  if (isReturned) {
    steps.push({
      ...RETURNED_STEP,
      state: 'returned' as StepState,
      event: eventByStep.get('returned') || null,
    });
    // Mark delivered as completed if it was reached
    const deliveredStep = steps.find(s => s.key === 'delivered');
    if (deliveredStep && deliveredStep.state === 'pending') {
      deliveredStep.state = 'completed';
    }
  }

  return steps;
}

/* ─── Color/style maps ─── */
const STATE_COLORS: Record<StepState, { dot: string; line: string; text: string }> = {
  completed: {
    dot: 'bg-[var(--color-success,#22c55e)]',
    line: 'bg-[var(--color-success,#22c55e)]',
    text: 'text-[var(--color-success,#22c55e)]',
  },
  active: {
    dot: 'bg-[var(--color-primary,#3b82f6)]',
    line: 'bg-[var(--color-border,#e5e7eb)]',
    text: 'text-[var(--color-primary,#3b82f6)]',
  },
  pending: {
    dot: 'bg-[var(--color-border,#e5e7eb)]',
    line: 'bg-[var(--color-border,#e5e7eb)]',
    text: 'text-[var(--color-text-muted)]',
  },
  error: {
    dot: 'bg-[var(--color-error,#ef4444)]',
    line: 'bg-[var(--color-border,#e5e7eb)]',
    text: 'text-[var(--color-error,#ef4444)]',
  },
  returned: {
    dot: 'bg-amber-500',
    line: 'bg-amber-500',
    text: 'text-amber-500',
  },
};

/* ─── Compact Mode ─── */
function CompactTracker({ steps }: { steps: ReturnType<typeof computeSteps> }) {
  const [tooltip, setTooltip] = useState<{ idx: number; x: number; y: number } | null>(null);

  return (
    <div className="relative inline-flex items-center gap-0" style={{ minWidth: '80px' }}>
      {steps.map((step, i) => {
        const colors = STATE_COLORS[step.state];
        const isLast = i === steps.length - 1;

        return (
          <div key={step.key} className="flex items-center">
            {/* Dot */}
            <div
              className="relative cursor-default"
              onMouseEnter={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setTooltip({ idx: i, x: rect.left + rect.width / 2, y: rect.top });
              }}
              onMouseLeave={() => setTooltip(null)}
            >
              <div
                className={`w-2 h-2 rounded-full ${colors.dot} ${
                  step.state === 'active' ? 'ring-2 ring-[var(--color-primary,#3b82f6)]/30' : ''
                }`}
              />
              {step.state === 'active' && (
                <div className="absolute inset-0 w-2 h-2 rounded-full bg-[var(--color-primary,#3b82f6)] animate-ping opacity-40" />
              )}
            </div>

            {/* Connector line */}
            {!isLast && (
              <div
                className={`h-0.5 flex-1 ${
                  step.state === 'completed' ? STATE_COLORS.completed.line : STATE_COLORS.pending.line
                }`}
                style={{ width: '12px' }}
              />
            )}
          </div>
        );
      })}

      {/* Tooltip */}
      {tooltip !== null && steps[tooltip.idx] && (
        <div
          className="fixed z-50 px-2 py-1 rounded text-[10px] bg-[var(--color-text)] text-[var(--color-bg)] shadow-lg whitespace-nowrap pointer-events-none"
          style={{
            left: tooltip.x,
            top: tooltip.y - 28,
            transform: 'translateX(-50%)',
          }}
        >
          {steps[tooltip.idx].label}
          {steps[tooltip.idx].event && (
            <span className="opacity-70 ml-1">
              {fmtShortDate(steps[tooltip.idx].event!.created_at)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Full Mode ─── */
function FullTracker({
  steps,
  trackingNumber,
  carrier,
  unitCost,
  deliveryDate,
  events,
}: {
  steps: ReturnType<typeof computeSteps>;
  trackingNumber?: string;
  carrier?: string;
  unitCost?: number;
  deliveryDate?: string;
  events: PurchaseEvent[];
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="space-y-3">
      {/* Progress bar */}
      <div className="flex items-start">
        {steps.map((step, i) => {
          const colors = STATE_COLORS[step.state];
          const isLast = i === steps.length - 1;
          const Icon = step.state === 'error' ? X
            : step.state === 'returned' ? RotateCcw
            : step.state === 'completed' ? Check
            : step.icon;

          return (
            <div key={step.key} className="flex items-start flex-1 last:flex-none">
              <div className="flex flex-col items-center">
                {/* Circle */}
                <div className={`relative flex items-center justify-center w-7 h-7 rounded-full ${colors.dot}`}>
                  <Icon size={14} className="text-white" strokeWidth={2.5} />
                  {step.state === 'active' && (
                    <div className="absolute inset-0 w-7 h-7 rounded-full bg-[var(--color-primary,#3b82f6)] animate-ping opacity-25" />
                  )}
                </div>
                {/* Label */}
                <span className={`text-[10px] font-medium mt-1.5 ${colors.text} text-center leading-tight`}>
                  {step.label}
                </span>
                {/* Date */}
                {step.event && (
                  <span className="text-[9px] text-[var(--color-text-muted)] mt-0.5">
                    {fmtShortDate(step.event.created_at)}
                  </span>
                )}
              </div>

              {/* Connector */}
              {!isLast && (
                <div className="flex-1 flex items-center mt-3.5 mx-1">
                  <div
                    className={`h-0.5 w-full rounded ${
                      step.state === 'completed' ? STATE_COLORS.completed.line : STATE_COLORS.pending.line
                    }`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Info bar */}
      {(carrier || trackingNumber || unitCost != null || deliveryDate) && (
        <div className="flex flex-wrap items-center gap-3 text-xs text-[var(--color-text-muted)] px-1">
          {carrier && <span>{carrier}</span>}
          {trackingNumber && (
            <span className="font-mono truncate max-w-[180px]" title={trackingNumber}>
              {trackingNumber}
            </span>
          )}
          {unitCost != null && <span>${Number(unitCost).toFixed(2)}</span>}
          {deliveryDate && (
            <span>{fmtShortDate(deliveryDate)}</span>
          )}
        </div>
      )}

      {/* Expandable events timeline */}
      {events.length > 0 && (
        <div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-[10px] text-[var(--color-primary)] hover:underline"
          >
            {expanded ? 'Ocultar historial' : `Ver historial (${events.length})`}
          </button>
          {expanded && (
            <div className="mt-2 ml-3 border-l-2 border-[var(--color-border-light)] pl-3 space-y-1.5">
              {events.map((ev, i) => (
                <div key={i} className="flex items-baseline gap-2 text-[10px]">
                  <span className="text-[var(--color-text-muted)] whitespace-nowrap">
                    {fmtFullDate(ev.created_at)}
                  </span>
                  <span className="font-medium text-[var(--color-text)]">
                    {ev.status}
                  </span>
                  {ev.previous_status && (
                    <span className="text-[var(--color-text-muted)]">
                      &larr; {ev.previous_status}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Main Component ─── */
export function PurchaseTracker({
  events,
  currentStatus,
  compact = false,
  trackingNumber,
  carrier,
  unitCost,
  deliveryDate,
}: PurchaseTrackerProps) {
  const steps = useMemo(
    () => computeSteps(events, currentStatus),
    [events, currentStatus]
  );

  if (compact) {
    return <CompactTracker steps={steps} />;
  }

  return (
    <FullTracker
      steps={steps}
      trackingNumber={trackingNumber}
      carrier={carrier}
      unitCost={unitCost}
      deliveryDate={deliveryDate}
      events={events}
    />
  );
}
